import type {
  UserType,
  LegacyUserType,
  PermissionLevel,
  FeaturePermissions,
  AccountUser,
  ExchangeUserPermission,
  Exchange,
} from '../types/exchange'
import type { User } from '../types/auth'

// ---------------------------------------------------------------------------
// Constants: User Type metadata
// ---------------------------------------------------------------------------

export const USER_TYPE_LABELS: Record<UserType, string> = {
  exchanger: 'Exchanger',
  qi_officer: 'QI Officer',
  advisor: 'Advisor',
  attorney: 'Attorney',
  accountant: 'Accountant',
  title_company: 'Title Company',
  lender: 'Lender',
  viewer: 'Viewer',
}

export const USER_TYPE_DESCRIPTIONS: Record<UserType, string> = {
  exchanger: 'Property owner doing the exchange — full access',
  qi_officer: 'QI staff managing the exchange — full access',
  advisor: 'Real estate advisor or broker — property and marketplace access',
  attorney: 'Legal counsel — documents and compliance access',
  accountant: 'CPA or tax professional — financial and IRS forms access',
  title_company: 'Title or escrow officer — document and closing access',
  lender: 'Bank or mortgage representative — read-only financial access',
  viewer: 'Read-only access to documents and forms',
}

export const ALL_USER_TYPES: UserType[] = [
  'exchanger',
  'qi_officer',
  'advisor',
  'attorney',
  'accountant',
  'title_company',
  'lender',
  'viewer',
]

// ---------------------------------------------------------------------------
// Constants: Feature metadata
// ---------------------------------------------------------------------------

export type FeatureKey = keyof FeaturePermissions

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  documents: 'Documents',
  forms: 'Forms',
  irs_forms: 'IRS Forms',
  calculator: 'Calculator',
  marketplace: 'Marketplace',
  wizard: 'Exchange Wizard',
  financials: 'Financials',
}

// ---------------------------------------------------------------------------
// Default permission matrix — aligned with migration default_feature_permissions()
// ---------------------------------------------------------------------------

export const DEFAULT_FEATURE_PERMISSIONS: Record<UserType, FeaturePermissions> = {
  exchanger: {
    documents: ['read', 'upload', 'sign'],
    forms: ['read', 'fill'],
    irs_forms: ['read'],
    calculator: ['use'],
    marketplace: ['browse'],
    wizard: ['use'],
    financials: ['view'],
  },
  qi_officer: {
    documents: ['read', 'upload', 'sign'],
    forms: ['read', 'fill'],
    irs_forms: ['read', 'generate'],
    calculator: ['use'],
    marketplace: ['browse'],
    wizard: ['use'],
    financials: ['view'],
  },
  advisor: {
    documents: ['read'],
    forms: ['read'],
    calculator: ['use'],
    marketplace: ['browse'],
    wizard: ['use'],
    financials: ['view'],
  },
  attorney: {
    documents: ['read', 'upload'],
    forms: ['read'],
    irs_forms: ['read'],
    financials: ['view'],
  },
  accountant: {
    documents: ['read'],
    forms: ['read'],
    irs_forms: ['read', 'generate'],
    calculator: ['use'],
    financials: ['view'],
  },
  title_company: {
    documents: ['read', 'upload', 'sign'],
    forms: ['read'],
    financials: ['view'],
  },
  lender: {
    documents: ['read'],
    forms: ['read'],
    financials: ['view'],
  },
  viewer: {
    documents: ['read'],
    forms: ['read'],
  },
}

export const DEFAULT_PERMISSION_LEVEL: Record<UserType, PermissionLevel> = {
  exchanger: 'write',
  qi_officer: 'write',
  advisor: 'write',
  attorney: 'read',
  accountant: 'read',
  title_company: 'write',
  lender: 'read',
  viewer: 'read_only',
}

// ---------------------------------------------------------------------------
// Legacy type mapping — converts old types to new ones
// ---------------------------------------------------------------------------

const LEGACY_TYPE_MAP: Record<LegacyUserType, UserType> = {
  owner: 'exchanger',
  accounting: 'accountant',
  real_estate_agent: 'advisor',
  member: 'exchanger',
  custom: 'viewer',
}

export function normalizeLegacyUserType(userType: UserType | LegacyUserType): UserType {
  if (userType in LEGACY_TYPE_MAP) {
    return LEGACY_TYPE_MAP[userType as LegacyUserType]
  }
  return userType as UserType
}

// ---------------------------------------------------------------------------
// Account role to default UserType mapping
// ---------------------------------------------------------------------------

const ACCOUNT_ROLE_TO_USER_TYPE: Record<AccountUser['role'], UserType> = {
  owner: 'exchanger',
  admin: 'qi_officer',
  member: 'exchanger',
  viewer: 'viewer',
}

// ---------------------------------------------------------------------------
// Resolved permissions interface
// ---------------------------------------------------------------------------

export interface ResolvedPermissions {
  accountRole: AccountUser['role'] | null
  exchangeUserType: UserType | null
  permissionLevel: PermissionLevel
  features: FeaturePermissions
  canRead: boolean
  canWrite: boolean
  isPlatformAdmin: boolean
  hasExchangeAccess: boolean
}

const FULL_ACCESS_PERMISSIONS: FeaturePermissions = {
  documents: ['read', 'upload', 'sign'],
  forms: ['read', 'fill'],
  irs_forms: ['read', 'generate'],
  calculator: ['use'],
  marketplace: ['browse'],
  wizard: ['use'],
  financials: ['view'],
}

const FULL_ACCESS: ResolvedPermissions = {
  accountRole: null,
  exchangeUserType: null,
  permissionLevel: 'write',
  features: FULL_ACCESS_PERMISSIONS,
  canRead: true,
  canWrite: true,
  isPlatformAdmin: false,
  hasExchangeAccess: true,
}

const NO_ACCESS: ResolvedPermissions = {
  accountRole: null,
  exchangeUserType: null,
  permissionLevel: 'read_only',
  features: {},
  canRead: false,
  canWrite: false,
  isPlatformAdmin: false,
  hasExchangeAccess: false,
}

// ---------------------------------------------------------------------------
// Core resolution function
// ---------------------------------------------------------------------------

export function resolvePermissions(
  user: User,
  accountUser: AccountUser | null,
  exchangePermission: ExchangeUserPermission | null,
): ResolvedPermissions {
  // Platform admin bypass
  if (user.isAdmin) {
    return {
      ...FULL_ACCESS,
      isPlatformAdmin: true,
      accountRole: accountUser?.role ?? null,
    }
  }

  // Backward compat: no permission records at all = legacy user, full access
  if (!accountUser && !exchangePermission) {
    return { ...FULL_ACCESS }
  }

  // Determine the effective user type
  let userType: UserType
  let permissionLevel: PermissionLevel
  let features: FeaturePermissions

  if (exchangePermission) {
    // Exchange-level permission takes precedence
    userType = normalizeLegacyUserType(exchangePermission.userType)
    permissionLevel = exchangePermission.permissionLevel ?? DEFAULT_PERMISSION_LEVEL[userType]

    // Use explicit feature permissions if set, otherwise use defaults for the user type
    if (exchangePermission.featurePermissions && hasAnyPermissions(exchangePermission.featurePermissions)) {
      features = normalizeFeaturePermissions(exchangePermission.featurePermissions)
    } else {
      features = { ...DEFAULT_FEATURE_PERMISSIONS[userType] }
    }
  } else if (accountUser) {
    // Derive from account role
    userType = ACCOUNT_ROLE_TO_USER_TYPE[accountUser.role]
    permissionLevel = DEFAULT_PERMISSION_LEVEL[userType]
    features = { ...DEFAULT_FEATURE_PERMISSIONS[userType] }
  } else {
    return { ...NO_ACCESS }
  }

  return {
    accountRole: accountUser?.role ?? null,
    exchangeUserType: userType,
    permissionLevel,
    features,
    canRead: permissionLevel !== 'read_only' || true, // all levels can read
    canWrite: permissionLevel === 'write',
    isPlatformAdmin: false,
    hasExchangeAccess: true,
  }
}

// ---------------------------------------------------------------------------
// Permission check helpers
// ---------------------------------------------------------------------------

export function canAccessFeature(
  permissions: ResolvedPermissions,
  feature: FeatureKey,
): boolean {
  if (permissions.isPlatformAdmin) return true
  if (!permissions.hasExchangeAccess) return false

  const value = permissions.features[feature]
  if (value === undefined || value === false) return false
  if (value === true) return true
  return Array.isArray(value) && value.length > 0
}

export function canPerformAction(
  permissions: ResolvedPermissions,
  feature: FeatureKey,
  action: string,
): boolean {
  if (permissions.isPlatformAdmin) return true
  if (!permissions.hasExchangeAccess) return false

  const value = permissions.features[feature]
  if (value === undefined || value === false) return false
  if (value === true) return true
  return Array.isArray(value) && value.includes(action as never)
}

export function canWriteFeature(
  permissions: ResolvedPermissions,
  feature: FeatureKey,
): boolean {
  if (!canAccessFeature(permissions, feature)) return false
  if (permissions.isPlatformAdmin) return true
  return permissions.canWrite
}

export function isPlatformAdmin(user: User): boolean {
  return user.isAdmin === true
}

// ---------------------------------------------------------------------------
// Exchange filtering
// ---------------------------------------------------------------------------

export function filterExchangesByAccess(
  exchanges: Exchange[],
  accountUsers: AccountUser[],
  _userId: string,
): Exchange[] {
  if (accountUsers.length === 0) {
    // No account memberships = legacy user, see all exchanges
    return exchanges
  }

  // Collect all allowed exchange IDs across all account memberships
  const hasUnrestricted = accountUsers.some(au => !au.allowedExchanges)
  if (hasUnrestricted) {
    return exchanges
  }

  const allowedIds = new Set<string>()
  for (const au of accountUsers) {
    if (au.allowedExchanges) {
      for (const id of au.allowedExchanges) {
        allowedIds.add(id)
      }
    }
  }

  return exchanges.filter(e => allowedIds.has(e.id))
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function hasAnyPermissions(fp: FeaturePermissions): boolean {
  return Object.values(fp).some(v => {
    if (v === true) return true
    if (Array.isArray(v) && v.length > 0) return true
    return false
  })
}

function normalizeFeaturePermissions(fp: FeaturePermissions): FeaturePermissions {
  const normalized: FeaturePermissions = {}
  for (const [key, value] of Object.entries(fp)) {
    if (value === true) {
      // Legacy boolean true — map to full actions for the feature
      normalized[key as FeatureKey] = getFullActionsForFeature(key as FeatureKey) as any
    } else if (value === false) {
      // Legacy boolean false — omit (no access)
    } else if (Array.isArray(value)) {
      normalized[key as FeatureKey] = value as any
    }
  }
  return normalized
}

function getFullActionsForFeature(feature: FeatureKey): string[] {
  switch (feature) {
    case 'documents': return ['read', 'upload', 'sign']
    case 'forms': return ['read', 'fill']
    case 'irs_forms': return ['read', 'generate']
    case 'calculator': return ['use']
    case 'marketplace': return ['browse']
    case 'wizard': return ['use']
    case 'financials': return ['view']
    default: return []
  }
}
